import { useOnboardingAnswersOptional } from '@/services/db/onboarding-answers/queries';
import { useIsAccountVerified } from './account-verification';
import { useEssentialsOnboardingGate } from './completion';
import { selectOnboardingRedirect, type OnboardingPageId } from './redirects';

export type OnboardingRedirectResult = {
  /** Where the page should send the user, or `null` to stay. */
  redirect: string | null;
  /**
   * True while the gate's signals load. The single hold condition for every
   * `/onboarding` entry point: callers must render a loader — neither acting
   * on `redirect` (it is withheld) nor rendering actionable content, or a
   * user could proceed on provisional eligibility.
   */
  isLoading: boolean;
};

/**
 * Stay-or-go decision for an `/onboarding/*` page. Pages render a
 * `SafeNavigate` off `redirect` and a loader off `isLoading`; the rules
 * themselves live in the pure `selectOnboardingRedirect`.
 *
 * TODO(SELF-135): when the verification signal becomes an async read, fold
 * its loading state into `isLoading` HERE (`useIsAccountVerified` returns a
 * bare boolean today) — this hook is the one place the hold condition is
 * defined for all entry points.
 */
export function useOnboardingRedirect(page: OnboardingPageId): OnboardingRedirectResult {
  const { isEligible, isComplete, isLoading: isGateLoading } = useEssentialsOnboardingGate();
  const isVerified = useIsAccountVerified();
  // Cohort-gated like completion.ts's gate queries — ineligible accounts fire no request.
  const answersQuery = useOnboardingAnswersOptional({ queryConfig: { enabled: isEligible } });

  // While loading, the inputs are provisional — redirecting on them could bounce
  // an eligible user home or re-ask an answered user. `isPending` settles on data
  // OR error (fail open to "not answered"); the `isEligible` guard matters
  // because a disabled query stays pending forever.
  if (isGateLoading || (isEligible && answersQuery.isPending)) {
    return { redirect: null, isLoading: true };
  }

  const hasAnswered = (answersQuery.data?.answers?.listen_targets?.length ?? 0) > 0;

  return {
    redirect: selectOnboardingRedirect({ page, isVerified, isEligible, isComplete, hasAnswered }),
    isLoading: false,
  };
}
